import { ReadGridRow, SelectedRows } from '@datasets/dataset-details/datasetData/context/types'
import { ErrorCorrectionResponse, ErrorCorrectionRow } from '@global/api/error-correction/type'
import { useErrorCorrection } from '@global/hooks/mutations/error-correction/useErrorCorrection'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router'
import { isFieldErrorObject } from 'error-correction'
import { SelectiveDatasetContext } from '@datasets/create-dataset/selective/context'
import { EventTrackingEventType, trackEvent } from 'lib/eventTracking'
import { useAppSelector } from '@redux/hooks'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'

export interface ErrorCorrectionContextType {
  correctErrors: (datasetId: string, rows: SelectedRows) => Promise<ErrorCorrectionResponse>
  isLoading: boolean
  errorCorrectionStats?: {
    rowCount: number
    errorCount: number
    correctedCount: number
  }
}

export const ErrorCorrectionContext = createContext<ErrorCorrectionContextType | null>(null)

const getPreviousErrors = (rows: ReadGridRow[]) =>
  rows.flatMap(row =>
    Object.entries(row.data).flatMap(
      ([fieldId, data]) => data.errors?.map(error => ({ ...error, cvid: row.cvid, fieldId })) ?? [],
    ),
  )

const getCorrectedCount = (
  currentErrors: unknown[],
  previousErrors: ReturnType<typeof getPreviousErrors>,
  errorCorrections: ErrorCorrectionRow[],
) => {
  const previousErrorSet = new Set(previousErrors.map(error => `${error.cvid}:${error.fieldId}`))
  const errorSet = new Set(
    currentErrors
      .filter(isFieldErrorObject)
      .map(({ cvid, field_id, cell_value }) => ({
        cvid,
        field_id,
        cell_value,
      }))
      .map(error => `${error.cvid}:${error.field_id}`),
  )

  let correctedCount = 0
  for (const correction of errorCorrections) {
    for (const [fieldId, field] of Object.entries(correction.fields)) {
      const lookupKey = `${correction.cvid}:${fieldId}`
      if (previousErrorSet.has(lookupKey) && field.modified && !errorSet.has(lookupKey)) {
        correctedCount++
      }
    }
  }

  return correctedCount
}

export const ErrorCorrectionProvider = ({ children }: React.PropsWithChildren) => {
  const {
    state: { errors, errorCount = 0 },
  } = useContext(SelectiveDatasetContext)
  const [hasGenAIEnabled] = useAppSelector(selectFeatureIncludes([FeatureDict.GEN_AI]))
  const [cvIds, setCvIds] = useState<Set<string>>(new Set())
  const [previousErrors, setPreviousErrors] = useState<ReturnType<typeof getPreviousErrors>>([])
  const { mutateAsync, isPending, data, reset } = useErrorCorrection()
  const location = useLocation()

  const previousPathnameRef = useRef<string>('')

  useEffect(() => {
    const currentPathname = location.pathname
    const previousPathname = previousPathnameRef.current

    if (previousPathname.includes('/selective') && !currentPathname.includes('/selective')) {
      setCvIds(new Set())
      setPreviousErrors([])
      reset()
    }

    previousPathnameRef.current = currentPathname
  }, [location.pathname, reset])

  const errorCorrections = useMemo(
    () => data?.rows.filter(error => cvIds.has(error.cvid)),
    [data, cvIds],
  )

  const correctErrors = useCallback(
    async (datasetId: string, rows: SelectedRows) => {
      const selectedRows = Object.values(rows).filter(row => typeof row !== 'number')
      const cvIds = selectedRows.map(row => row.cvid)
      const prevErrors = getPreviousErrors(selectedRows)

      return mutateAsync(
        { datasetId, request: { cvIds } },
        {
          onSuccess: () => {
            setCvIds(new Set(cvIds))
            setPreviousErrors(prevErrors)
          },
          onSettled: data => {
            const correctedCount = getCorrectedCount(errors, prevErrors, data?.rows ?? [])
            trackEvent(EventTrackingEventType.SMART_CORRECT_REQUESTED, {
              datasetId,
              genAIEnabled: !!hasGenAIEnabled,
              rowCount: selectedRows.length,
              errorCount: errorCount + correctedCount,
              correctedCount,
            })
          },
        },
      )
    },
    [mutateAsync, hasGenAIEnabled, errors, errorCount],
  )

  const errorCorrectionStats = useMemo(() => {
    if (!errorCorrections?.length) {
      return
    }
    const correctedCount = getCorrectedCount(errors, previousErrors, errorCorrections)
    return {
      rowCount: errorCorrections.length,
      errorCount: errorCount + correctedCount,
      correctedCount,
    }
  }, [errors, errorCorrections, previousErrors, errorCount])

  const contextValue = useMemo(
    () => ({
      correctErrors,
      isLoading: isPending,
      errorCorrectionStats,
    }),
    [correctErrors, isPending, errorCorrectionStats],
  )

  return (
    <ErrorCorrectionContext.Provider value={contextValue}>
      {children}
    </ErrorCorrectionContext.Provider>
  )
}
